import type { InjectionKey, Ref } from "vue";

export type InvitationMode = "guest" | "public";

/**
 * Contract context runtime ringan untuk halaman undangan.
 *
 * Context ini sengaja kecil agar dependency antar flow tetap eksplisit dan
 * tidak berubah menjadi tempat menyimpan state global yang terlalu banyak.
 */
interface InvitationContext {
  isEventPreview: Ref<boolean>;
  invitationMode: Ref<InvitationMode>;
  recipientName: Ref<string>;
  isGuestBased: Ref<boolean>;
}

const invitationContextKey = Symbol("invitation-context") as InjectionKey<InvitationContext>;

/**
 * Mendaftarkan context undangan pada level page/bootstrap.
 *
 * Function ini dipanggil dari `useInvitationPage()` sebelum composable lain
 * dibaca, sehingga flow section-level bisa mengakses context yang sama tanpa
 * bergantung pada `inject("...")` string literal.
 *
 * @param context Nilai context yang ingin dibagikan ke subtree undangan
 * @returns Context yang sama agar mudah dipakai dalam chaining bila diperlukan
 */
export function provideInvitationContext(context: InvitationContext) {
  provide(invitationContextKey, context);

  return context;
}

/**
 * Mengambil context undangan yang sudah disediakan oleh page bootstrap.
 *
 * Gunakan helper ini daripada `inject("...")` string literal agar dependency
 * lebih aman, typed, dan mudah dilacak saat refactor.
 *
 * @returns Context undangan aktif untuk subtree saat ini
 * @throws Error bila dipanggil di luar provider undangan
 */
export function useInvitationContext() {
  const context = inject(invitationContextKey, null);

  if (!context) {
    throw new Error("useInvitationContext must be used within the invitation page context.");
  }

  return context;
}
